import { Cancel, CheckCircle } from '@mui/icons-material';
import { Box, Card, CardContent, Chip, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import Answers from '@/components/exercise/answers';
import ExplanationGeneral from '@/components/exercise/explanation-general';
import ExplanationToggle from '@/components/exercise/explanation-toggle';
import ReportContentButton from '@/components/shared/report-content-button';
import RichContent from '@/components/shared/rich-content';
import TooltipModal from '@/components/shared/tooltip-modal';
import { useReportContent } from '@/hooks';
import type { Question as QuestionModel } from '@/types';

export default function Question({
  index,
  result,
  preview,
  question,
}: {
  index: number;
  result?: boolean;
  preview?: boolean;
  question: QuestionModel;
}) {
  const { id, correct, labels, questionNode } = question;
  const { reportContent, shouldShow } = useReportContent();

  return (
    <Card
      sx={{
        mb: 4,
        px: 2,
        py: 1.2,
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'grey.500',
        },
        transition: 'border-color 0.4s ease-in-out',
      }}
      variant="outlined">
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 14, userSelect: 'none' }}>#{index + 1}</Typography>
            <ReportContentButton
              entityId={id}
              entityType="Question"
              onReport={reportContent}
              show={shouldShow}
              variant="inline"
            />
          </Box>
          <Stack direction="row" spacing={1}>
            {labels && labels.length > 0 && (
              <Stack spacing={1} direction="row">
                {labels.map((label) => (
                  <Chip label={label} key={label} size="small" variant="outlined" />
                ))}
              </Stack>
            )}
            {result && (
              <Box className={correct ? 'correct' : 'incorrect'}>
                {correct ? (
                  <TooltipModal title="Alle Antworten richtig markiert">
                    <CheckCircle fontSize="small" sx={{ color: '#00DFA3' }} />
                  </TooltipModal>
                ) : (
                  <TooltipModal title="Nicht alle Antworten richtig markiert">
                    <Cancel fontSize="small" sx={{ color: '#FC5055' }} />
                  </TooltipModal>
                )}
              </Box>
            )}
          </Stack>
        </Box>
        <Box py={1.5}>
          <Typography
            component="h3"
            sx={{
              fontSize: 16,
              userSelect: 'none',
            }}>
            <RichContent content={questionNode} />
          </Typography>
        </Box>
      </Box>
      <CardContent sx={{ p: 0 }}>
        {result && <ExplanationGeneral question={question} />}
        <Answers question={question} result={result} preview={preview} />
      </CardContent>
      {result && <ExplanationToggle question={question} />}
    </Card>
  );
}
